import { useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PrintIcon from '@mui/icons-material/Print';
import axiosClient from '../../../api/axiosClient.js';
import { useLiveMatch } from '../LiveMatchContext.tsx';
import { printMatchReport } from '../report/printMatchReport.ts';

function formatDuration(ms: number): string {
  if (ms <= 0) return '—';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.75,
        px: 1.5,
        borderRadius: 1,
        bgcolor: 'rgba(255,255,255,0.04)',
      }}
    >
      <Typography sx={{ fontSize: '0.8rem', opacity: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{value}</Typography>
    </Box>
  );
}

export default function MatchEndScreen({ onSaveSuccess }: { onSaveSuccess?: () => void }) {
  const { state } = useLiveMatch();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    if (!state.eventDate) {
      setSaveError('Data evento non disponibile — riapri la partita e riprova.');
      setSaving(false);
      return;
    }
    try {
      const isOurHome = state.ourTeamSide === 'HOME';
      const ourCallup = isOurHome ? state.homeCallup : state.awayCallup;

      const finalResult = isOurHome
        ? `${state.setsWon.home}-${state.setsWon.away}`
        : `${state.setsWon.away}-${state.setsWon.home}`;

      const sets = state.completedSets.map((s) => ({
        id: 0,
        setNumber: s.setNumber,
        teamScore: isOurHome ? s.homePoints : s.awayPoints,
        opponentScore: isOurHome ? s.awayPoints : s.homePoints,
      }));

      // Mappa set → ID dei giocatori entrati come sostituti
      const ourSubType = isOurHome ? 'SUBSTITUTION_HOME' : 'SUBSTITUTION_AWAY';
      const subIdsBySet = new Map<number, Set<number>>();
      state.eventLog.forEach((e) => {
        if (e.type === ourSubType && e.substituteId !== undefined) {
          if (!subIdsBySet.has(e.setNumber)) subIdsBySet.set(e.setNumber, new Set());
          subIdsBySet.get(e.setNumber)!.add(e.substituteId);
        }
      });

      const ourLiberoId = isOurHome ? state.homeLiberoId : state.awayLiberoId;

      const participants = state.completedSets.flatMap((s) => {
        const ourLineup = isOurHome ? s.homeLineup : s.awayLineup;
        const subIds    = subIdsBySet.get(s.setNumber) ?? new Set<number>();
        const lineupIds = new Set(ourLineup.map((fp) => fp.playerId));

        // Giocatori nel lineup finale (titolari rimasti + subentrati)
        const lineupParts = ourCallup
          .filter((p) => p.playerEntityId && lineupIds.has(p.id))
          .map((p) => {
            const fp = ourLineup.find((f) => f.playerId === p.id)!;
            return { id: 0, setNumber: s.setNumber, playerId: p.playerEntityId!, role: fp.role || 'BANDA', starter: !subIds.has(p.id) };
          });

        // Titolari usciti per sostituzione — ricaviamo ruolo dallo snapshot dell'evento
        const exitedParts = state.eventLog
          .filter((e) => e.type === ourSubType && e.setNumber === s.setNumber && e.playerId !== undefined)
          .map((e) => {
            const p = ourCallup.find((pl) => pl.id === e.playerId);
            if (!p?.playerEntityId) return null;
            const snapLineup = isOurHome ? e.snapshot.homeLineup : e.snapshot.awayLineup;
            const fp = snapLineup.find((f) => f.playerId === e.playerId);
            return { id: 0, setNumber: s.setNumber, playerId: p.playerEntityId, role: fp?.role || 'BANDA', starter: true };
          })
          .filter((x): x is NonNullable<typeof x> => x !== null);

        // Libero in panchina (non in lineup): aggiunto come titolare con ruolo LIBERO
        const liberoOnBench = ourLiberoId && !lineupIds.has(ourLiberoId);
        const liberoPart = liberoOnBench
          ? (() => {
              const lp = ourCallup.find((p) => p.id === ourLiberoId);
              return lp?.playerEntityId
                ? { id: 0, setNumber: s.setNumber, playerId: lp.playerEntityId, role: 'LIBERO', starter: true }
                : null;
            })()
          : null;

        return [...lineupParts, ...exitedParts, ...(liberoPart ? [liberoPart] : [])];
      });

      await axiosClient.post('/matches/create', {
        matchId: 0,
        // l'evento identifica la partita anche con più eventi nello stesso giorno;
        // per le amichevoli il BE imposta da sé il tipo FRIENDLY
        eventId: state.eventId,
        date: state.eventDate,
        finalResult,
        competitionType: 'TOURNAMENT',
        notes: '',
        sets,
        participants,
      });

      onSaveSuccess?.();
    } catch (err: any) {
      const detail = err.response?.data?.message ?? err.response?.data ?? err.message ?? 'Errore sconosciuto';
      setSaveError(String(detail));
    } finally {
      setSaving(false);
    }
  }

  const winnerIsHome = state.setsWon.home > state.setsWon.away;
  const winnerName   = winnerIsHome ? state.homeTeamName  : state.awayTeamName;
  const winnerColor  = winnerIsHome ? state.homeShirtColor : state.awayShirtColor;

  const totalDurationMs = state.completedSets.reduce((sum, s) => sum + (s.durationMs ?? 0), 0);
  const longestSet      = state.completedSets.reduce<typeof state.completedSets[0] | null>(
    (max, s) => (!max || s.durationMs > max.durationMs ? s : max), null,
  );
  const totalPoints = state.completedSets.reduce((sum, s) => sum + s.homePoints + s.awayPoints, 0);
  const homeSubs    = state.eventLog.filter((e) => e.type === 'SUBSTITUTION_HOME').length;
  const awaySubs    = state.eventLog.filter((e) => e.type === 'SUBSTITUTION_AWAY').length;

  return (
    <Box
      sx={{
        height: '100%',
        bgcolor: '#1a1a2e',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        color: 'white',
        p: 3,
        overflowY: 'auto',
        animation: 'match-end-enter 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both',
        '@keyframes match-end-enter': {
          '0%':   { transform: 'scale(0.85)', opacity: 0 },
          '100%': { transform: 'scale(1)',    opacity: 1 },
        },
      }}
    >
      <EmojiEventsIcon sx={{ fontSize: { xs: 40, sm: 56 }, color: '#FFD700' }} />

      <Typography sx={{ fontSize: { xs: '0.8rem', sm: '1rem' }, opacity: 0.4, letterSpacing: 4, textTransform: 'uppercase' }}>
        The winner is
      </Typography>

      <Typography
        sx={{
          fontSize: { xs: '2rem', sm: '3.5rem' },
          fontWeight: 900,
          color: winnerColor,
          textAlign: 'center',
          lineHeight: 1.1,
        }}
      >
        {winnerName}
      </Typography>

      <Typography sx={{ fontSize: { xs: '1.4rem', sm: '2rem' }, fontWeight: 700, color: '#FFD700' }}>
        {state.setsWon.home} – {state.setsWon.away}
      </Typography>

      {/* Punteggi per set */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'center' }}>
        {state.completedSets.map((s) => {
          const setWinHome = s.homePoints > s.awayPoints;
          return (
            <Chip
              key={s.setNumber}
              label={`Set ${s.setNumber}: ${s.homePoints}–${s.awayPoints}`}
              size="small"
              sx={{
                bgcolor: setWinHome ? `${state.homeShirtColor}33` : `${state.awayShirtColor}33`,
                color: 'rgba(255,255,255,0.75)',
                fontSize: '0.75rem',
                border: '1px solid',
                borderColor: setWinHome ? `${state.homeShirtColor}66` : `${state.awayShirtColor}66`,
              }}
            />
          );
        })}
      </Box>

      <Divider sx={{ width: '60%', borderColor: 'rgba(255,255,255,0.12)', my: 0.5 }} />

      {/* Statistiche partita */}
      <Stack spacing={0.75} sx={{ width: '100%', maxWidth: 380 }}>
        {totalDurationMs > 0 && (
          <StatRow label="Durata partita" value={formatDuration(totalDurationMs)} />
        )}
        {longestSet && longestSet.durationMs > 0 && (
          <StatRow
            label="Set più lungo"
            value={`Set ${longestSet.setNumber} · ${formatDuration(longestSet.durationMs)}`}
          />
        )}
        <StatRow label="Punti totali" value={String(totalPoints)} />
        {(homeSubs > 0 || awaySubs > 0) && (
          <StatRow
            label="Sostituzioni"
            value={`${state.homeTeamName} ${homeSubs} — ${state.awayTeamName} ${awaySubs}`}
          />
        )}
      </Stack>

      {saveError && (
        <Alert severity="error" sx={{ width: '100%', maxWidth: 420, mt: 1 }}>
          {saveError}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={1}>
        <Button
          variant="outlined"
          size="large"
          startIcon={<PrintIcon />}
          onClick={() => printMatchReport(state)}
          sx={{ fontWeight: 700, px: 3, borderColor: 'rgba(255,255,255,0.4)', color: 'rgba(255,255,255,0.8)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}
        >
          Stampa referto
        </Button>
        <Button
          variant="contained"
          color="success"
          size="large"
          sx={{ fontWeight: 700, px: 5 }}
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {saving ? 'Salvataggio…' : 'Salva risultato'}
        </Button>
      </Stack>

    </Box>
  );
}
